import { Box } from '@atlaskit/primitives';
import { ConfluencePageAri } from '@atlassian/ari';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import PagePicker from '@databases/content-picker/PagePicker';
import { getCloudId, useDatabaseContent } from '@databases/database-state';
import {
  EditableListWithPlaceholder,
  type EditableListProps,
} from '@databases/editable-list';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { loadContent } from '@databases/redux/connections/actions';
import { selectContentStatus } from '@databases/redux/connections/selectors';
import {
  type Content,
  type ResolvedContentReference,
  type OptionalCreatePageReferenceOrContentId,
} from '@databases/types/confluence';
import {
  CUSTOM_TEMPLATE_EMPTY,
  CUSTOM_TEMPLATE_ENTRY_MACRO,
  DatabaseFieldType,
  PageSubType,
  ParentPageOption,
  type RawDatabaseValue,
} from '@databases/types/database';
import { isPageReference } from '@databases/utils/confluence/utils';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import {
  createPageWithOptions,
  resolveSinglePageLinkValue,
} from '@databases/utils/database/field-types/pageLink';
import { getDatabaseEntryOptional } from '@databases/utils/database/utils';
import { addContentToList } from '@databases/utils/redux/content/actions';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import { getEntryPath } from '@databases/utils/urls';
import { generateId } from '@databases/utils/utils';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { retryWithBackoff } from '@canvas/retry';

import { type EditComponentProps } from '../types';

import { useDynamicParent, useFixedParentPage } from './hooks';

type Value = RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0];

const getId = (value: Value): string => value._id;

const getNewValue = (): Value => ({ _id: generateId() });

const validate = (creationErrorIndex: number | null, currentIndex: number) => {
  return !(creationErrorIndex === currentIndex);
};

const PageLinkValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.PAGE_LINK>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  contentId,
  entryId,
  isSeamless,
  isFilterValueEdit,
}) => {
  const [pendingPages, setPendingPages] = useState<string[]>([]);
  const [creationErrorIndex, setCreationErrorIndex] = useState<number | null>(null);

  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const client = useAnalytics();
  const { spaceKey } = useDatabaseContent();
  const { formatMessage } = useIntl();

  const [inputValue, setInputValue] = useState<Value[]>(value);

  const [database, entry, entryUrl] = defaultStoreUtils.useEqualSelector((state) => {
    const database =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, {
            contentId,
          })
        : undefined;
    const entry =
      database && entryId != null
        ? getDatabaseEntryOptional(database, entryId)
        : undefined;
    const baseUrl = selectBaseUrl(state);
    const entryUrl =
      database && entry
        ? baseUrl +
          getEntryPath({
            contentId: database.contentId,
            entryId: entry.id,
            spaceKey: database.space.key,
          })
        : undefined;

    return [database, entry, entryUrl];
  });

  const noParentItem =
    field.settings.parentPageOption === ParentPageOption.FIXED &&
    !field.settings.parentPage &&
    !field.settings.isParentPageSourceDatabase;
  const creationErrorMessage = noParentItem
    ? formatMessage(
        messages['field-settings.type-page-link-create-inline-error.no-parent-item']
      )
    : undefined;

  const { isPending: isFixedPagePending, pageRef: fixedPageRef } = useFixedParentPage({
    contentId,
    field,
    entryId,
  });

  const { isPending: isDynamicParentPending, parentRef: dynamicParentPageRef } =
    useDynamicParent();

  // isParentPageSourceDatabase defaults to true so we need to add additional
  // checks before using the database as a parent page
  const resolvedIsParentPageSourceDatabase =
    field.settings.isParentPageSourceDatabase &&
    !field.settings.parentPage &&
    field.settings.creatable;

  const parentPageIsPending = resolvedIsParentPageSourceDatabase
    ? false
    : isFixedPagePending || isDynamicParentPending;

  const creationParentPage = useMemo(() => {
    if (field.settings.parentPageOption === ParentPageOption.DYNAMIC) {
      return dynamicParentPageRef;
    }
    if (resolvedIsParentPageSourceDatabase) {
      return {
        type: 'database',
        spaceKey,
        contentTitle: database?.name,
        contentId,
      } as ResolvedContentReference;
    }
    return fixedPageRef;
  }, [
    contentId,
    database?.name,
    dynamicParentPageRef,
    field.settings.parentPageOption,
    fixedPageRef,
    resolvedIsParentPageSourceDatabase,
    spaceKey,
  ]);

  const normalizedValue = defaultStoreUtils.useEqualSelector((state) =>
    inputValue.map((v) => resolveSinglePageLinkValue(v, state))
  );

  const getAriForPageContentId = useCallback(
    (contentId: string) =>
      ConfluencePageAri.create({
        pageId: contentId,
        siteId: getCloudId() ?? '',
      }).toString(),
    []
  );

  const isMulti = normalizedValue.length > 1 || field.settings.multi;
  const isLiveDocEnabled = isFeatureFlagEnabled('databases-frontend-live-doc');

  const createNewPage = useCallback(
    async (value: Value, index: number) => {
      if ('type' in value && value.type === 'page' && value.create && entry && database) {
        const newPendingPages = [...pendingPages, value._id];
        setPendingPages(newPendingPages);

        const customTemplateIds = [
          CUSTOM_TEMPLATE_ENTRY_MACRO.templateId,
          CUSTOM_TEMPLATE_EMPTY.templateId,
        ];

        const finalTemplateId = customTemplateIds.includes(
          field.settings.pageTemplate.templateId
        )
          ? null
          : field.settings.pageTemplate.templateId;

        try {
          const spaceKey = value.spaceKey;

          const parentId = value.create.parentPageId as string;

          const newPageFromMutation = await createPageWithOptions({
            templateId: finalTemplateId,
            entryUrl: field.settings.displayEntryOnPage ? entryUrl ?? null : null,
            spaceKey,
            parentId,
            subType: isLiveDocEnabled ? field.settings.pageSubType : null,
            contentTitle: value.contentTitle,
          });

          if (!newPageFromMutation) {
            return undefined;
          }
          const newPage: Content = newPageFromMutation;

          client.sendCreatePageFromDatabaseEvent({
            contentId,
            newPageId: newPage.id,
            databaseContainer: getDatabaseContainerType(),
            parentId,
            pageLocation: field.settings.parentPageOption,
            templateId: finalTemplateId,
            hasEntryEmbed: field.settings.displayEntryOnPage ?? false,
            pageSubType: isLiveDocEnabled ? field.settings.pageSubType : PageSubType.PAGE,
          });

          client.sendPageCreatedByFlowEvent({
            contentId: newPage.id,
            databaseContainer: getDatabaseContainerType(),
            isLivePage: isLiveDocEnabled
              ? field.settings.pageSubType === PageSubType.LIVE
              : false,
          });

          // We need to wait for the content to be distributed across services and become available for datasources
          // In the future it can be replaced with a better content loading indicator and automated data refresh
          retryWithBackoff({
            callback: () => {
              return new Promise((resolve, reject) => {
                dispatch(
                  loadContent({
                    ari: getAriForPageContentId(newPage.id),
                    connectionId: field.id,
                    forceReload: true,
                  })
                );
                const contentStatus = selectContentStatus(
                  store.getState().connections,
                  getAriForPageContentId(newPage.id)
                );
                if (contentStatus?.status !== AsyncOperationStatus.SUCCEEDED) {
                  return reject(new Error('Content not loaded'));
                }

                resolve(true);
              });
            },
            shouldRetry: () => true,
            maxRetries: 10,
          });

          dispatch(addContentToList(newPage));
          return newPage;
          // eslint-disable-next-line canvas/no-swallow-errors-in-catch
        } catch {
          setCreationErrorIndex(index);
        } finally {
          setPendingPages((pendingPages) => pendingPages.filter((p) => p !== value._id));
        }
      }
    },
    [
      entry,
      database,
      pendingPages,
      field,
      entryUrl,
      isLiveDocEnabled,
      client,
      contentId,
      dispatch,
      getAriForPageContentId,
      store,
    ]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    setCreationErrorIndex(null);
  }, [onBlur]);

  const handleInputChange = useCallback(() => {
    setCreationErrorIndex(null);
  }, []);

  const handleSingleChange = useCallback(
    async (val: OptionalCreatePageReferenceOrContentId | undefined) => {
      if (!val) {
        if (normalizedValue.length > 0) {
          setInputValue([]);
          onChange([], {
            type: DatabaseFieldType.PAGE_LINK,
            delta: {
              type: MultiValueChangeType.REMOVE,
              index: 0,
            },
          });
        }
      } else {
        const id = normalizedValue.length > 0 ? normalizedValue[0]._id : generateId();
        const newPage = await createNewPage({ ...val, _id: id }, 0);

        if (newPage) {
          val = {
            ...val,
            ari: getAriForPageContentId(newPage.id),
            contentId: newPage.id,
          };
        }

        const value = {
          ...(val ?? {}),
          _id: id,
        };

        setInputValue([value]);

        onChange([value], {
          type: DatabaseFieldType.PAGE_LINK,
          delta:
            normalizedValue.length > 0
              ? {
                  type: MultiValueChangeType.EDIT,
                  index: 0,
                  value,
                }
              : {
                  type: MultiValueChangeType.APPEND,
                  value,
                },
        });
      }
    },
    [normalizedValue, onChange, createNewPage, getAriForPageContentId]
  );

  const onItemChange = useCallback(
    async (value: Value, index: number, _: null, allValues: Value[]) => {
      const newPage = await createNewPage(value, index);

      if (newPage) {
        value = {
          _id: value._id,
          ari: getAriForPageContentId(newPage.id),
          contentId: newPage.id,
        };
      }

      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.PAGE_LINK,
        delta: {
          type: MultiValueChangeType.EDIT,
          value,
          index,
        },
      });
    },
    [createNewPage, getAriForPageContentId, onChange]
  );

  const onItemAdd = useCallback(
    async (value: Value, allValues: Value[]) => {
      const newPage = await createNewPage(value, allValues.indexOf(value));

      if (newPage) {
        value = {
          _id: value._id,
          ari: getAriForPageContentId(newPage.id),
          contentId: newPage.id,
        };
      }

      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.PAGE_LINK,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: value,
        },
      });
    },
    [createNewPage, getAriForPageContentId, onChange]
  );

  const onItemDelete = useCallback(
    (_: Value, index: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.PAGE_LINK,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index,
        },
      });
    },
    [onChange]
  );

  const onItemReorder = useCallback(
    (_: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.PAGE_LINK,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex,
          newIndex,
        },
      });
    },
    [onChange]
  );

  const getErrorMessage = useCallback(
    (i: number) => {
      if (!validate(creationErrorIndex, i)) {
        if (isLiveDocEnabled && field.settings.pageSubType === PageSubType.LIVE) {
          return formatMessage(
            messages['field-settings.type-page-link-live-doc-create-in-context-error']
          );
        } else {
          return formatMessage(
            messages['field-settings.type-page-link-create-in-context-error']
          );
        }
      }

      return '';
    },
    [creationErrorIndex, field.settings.pageSubType, formatMessage, isLiveDocEnabled]
  );

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, i, autoFocus, onChange }) => (
      <Box style={{ width: '100%' }}>
        <ValidationStatus
          error={getErrorMessage(i)}
          isTouched={!validate(creationErrorIndex, i)}
        >
          {(props) => (
            <PagePicker
              {...props}
              value={
                ('contentTitle' in value && isPageReference(value)) ||
                ('contentId' in value && !('type' in value))
                  ? value
                  : undefined
              }
              onChange={(val) => {
                onChange({ ...(val ?? {}), _id: value._id }, null);
              }}
              onInputChange={handleInputChange}
              preserveInputValue={!('contentTitle' in value || 'contentId' in value)}
              autoFocus={autoFocus}
              creationParentPage={creationParentPage}
              allowCreation={isFilterValueEdit ? false : field.settings.creatable}
              creationErrorMessage={creationErrorMessage}
              isPending={parentPageIsPending}
              isSeamless
              isLoading={pendingPages.indexOf(value._id) > -1}
              isMulti={isMulti}
              pageSubType={field.settings.pageSubType}
              isLiveDocEnabled={isLiveDocEnabled}
            />
          )}
        </ValidationStatus>
      </Box>
    ),
    [
      creationErrorIndex,
      creationErrorMessage,
      creationParentPage,
      field.settings.creatable,
      field.settings.pageSubType,
      getErrorMessage,
      handleInputChange,
      isFilterValueEdit,
      isLiveDocEnabled,
      isMulti,
      parentPageIsPending,
      pendingPages,
    ]
  );

  if (isMulti) {
    return (
      <EditableListWithPlaceholder<Value, null>
        getId={getId}
        values={normalizedValue}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={inputValue.length === 0 ? autoFocus : undefined}
        addLabel={
          isLiveDocEnabled && field.settings.pageSubType === PageSubType.LIVE
            ? formatMessage(messages['field-settings.type-page-link-add-live-doc'])
            : formatMessage(messages['field-settings.type-page-link-add-page'])
        }
        noAdd={!field.settings.multi}
        onBlur={handleBlur}
        onFocus={onFocus}
      />
    );
  } else {
    return (
      <ValidationStatus
        error={getErrorMessage(0)}
        isTouched={!validate(creationErrorIndex, 0)}
      >
        {(props) => (
          <PagePicker
            {...props}
            value={
              normalizedValue.length > 0
                ? ('contentTitle' in normalizedValue[0] &&
                    isPageReference(normalizedValue[0])) ||
                  ('contentId' in normalizedValue[0] && !('type' in normalizedValue[0]))
                  ? normalizedValue[0]
                  : undefined
                : undefined
            }
            onInputChange={handleInputChange}
            onChange={handleSingleChange}
            onFocus={onFocus}
            onBlur={handleBlur}
            autoFocus={autoFocus}
            creationParentPage={creationParentPage}
            allowCreation={isFilterValueEdit ? false : field.settings.creatable}
            creationErrorMessage={creationErrorMessage}
            isPending={parentPageIsPending}
            isSeamless={isSeamless}
            preserveInputValue={
              normalizedValue.length > 0
                ? !(
                    'contentTitle' in normalizedValue[0] ||
                    'contentId' in normalizedValue[0]
                  )
                : undefined
            }
            isLoading={pendingPages.length > 0}
            pageSubType={field.settings.pageSubType}
            isLiveDocEnabled={isLiveDocEnabled}
          />
        )}
      </ValidationStatus>
    );
  }
};

export default PageLinkValueEdit;
